import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import fr from 'facet-rest';
import useUserContext from 'Context/UserContext';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import PostAddIcon from '@material-ui/icons/PostAdd';
import { useTemplatesStateContext } from 'Context/TemplateContext';
import { pick } from 'Helpers/Affordances';
import NestedMenuItem from 'Components/UI/NestedMenuItem';
import ConfirmationDialog from 'Components/UI/ConfirmationDialog';
import { DataValueMap } from 'Helpers/Testing';
import { FDBox, FDSecondaryActionIconButton } from '@facet-wealth/design';
import NewTemplateDialog from './NewTemplateDialog';
import useTemplateOptionsMenu from './useTemplateOptionsMenu';
import TemplateList from './TemplateList';
import TemplateSearchInput from './TemplateSearchInput';
import { TEMPLATE_MENU_ICON_OPTIONS, TEMPLATE_TYPES } from './utils';

const styles = theme => ({
  root: {},
  menuItemLeftIcon: {
    marginRight: theme.spacing(1),
  },
  menuButton: {
    color: theme.palette.facet.primaryBlue,
  },
  templateSearch: {
    padding: theme.spacing(0, 1, 1.5),
    borderBottom: `1px solid ${theme.palette.facet.lightGray}`,
  },
});

const TemplateOptionsMenu = ({
  className,
  classes,
  onInsertTemplate,
  onInsertToken,
  templateDraftContent,
  templateSubject,
  ButtonProps,
  templateType,
  confirmBeforeInsert,
  menuIcon,
  menuIconSize,
  showTokenMenuOptionOnly,
  useLegacyButton,
}) => {
  // Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    visible: false,
    action: null,
  });

  const [newTemplateDialogOpen, setNewTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const {
    templates,
    templatesLoading,
    pinnedTemplates,
    templateDictionary,
    allTemplatesCurrentPageNumber,
    setAllTemplatesCurrentPageNumber,
    handlePaginationChangeForAllTemplates,
    loadingAllTemplatesPrevOrNextPage,
    setAnchorElement,
    anchorElement,
    isOpen,
    setTemplatesCollection,
    isInitializing,
  } = useTemplatesStateContext();

  const { agentResource } = useUserContext();

  // Template actions from hook
  const {
    saveTemplate,
    removeTemplate,
    overwriteTemplate,
    togglePinTemplate,
    searchTemplates,
  } = useTemplateOptionsMenu(anchorElement);

  // Sets anchor element for the template menu
  const handleOptionsMenuClick = (event) => {
    setAnchorElement(event.currentTarget);
  };

  // Close Menu - Removes anchor element
  const closeOptionsMenu = () => setAnchorElement(null);

  // Save draft as new template dialog
  const toggleTemplateDialog = () => {
    closeOptionsMenu();
    setNewTemplateDialogOpen(!newTemplateDialogOpen);
  };

  // Dispatches onInsertTemplate callback with template content
  const handleInsertTemplate = (templateRes) => {
    if (confirmBeforeInsert) {
      setSelectedTemplate(templateRes);
      setConfirmDialog({
        action: 'insert',
        visible: true,
      });
    } else {
      onInsertTemplate(templateRes);
    }
    closeOptionsMenu();
  };

  // Remove template - Show confirmation
  const handleTemplateRemoval = (templateRes) => {
    setSelectedTemplate(templateRes);
    setConfirmDialog({
      action: 'remove',
      visible: true,
    });
    closeOptionsMenu();
  };

  // Overwrite template - Show confirmation
  const handleTemplateOverwrite = (templateRes) => {
    setSelectedTemplate(templateRes);
    setConfirmDialog({
      action: 'overwrite',
      visible: true,
    });
    closeOptionsMenu();
  };

  const handleInsertToken = (tokenResource) => {
    onInsertToken(tokenResource.attr('token'));
    closeOptionsMenu();
  };

  // Create new template from draft
  const handleCreateNewTemplate = templateName => {
    saveTemplate(templateName, templateDraftContent, templateSubject, templateType);
    toggleTemplateDialog();
  };

  // Returns pinned template list if pinnedMessageTemplates rel. exists
  const PinnedTemplatesListComponent = pick(agentResource, {
    [fr.relationships.pinnedMessageTemplates.name]: (
      <TemplateList
        menuTitle={'Pinned Templates'}
        templateCollection={pinnedTemplates}
        listItemOnClick={handleInsertTemplate}
        togglePinTemplate={togglePinTemplate}
        listType={'insert-pinned'}
        placeholder={'No pinned templates'}
      />
    ),
  }, null);

  const confirmationActions = {
    insert: () => onInsertTemplate(selectedTemplate),
    remove: () => removeTemplate(selectedTemplate.resourceId, templateDraftContent),
    overwrite: () => overwriteTemplate(selectedTemplate.resourceId, templateDraftContent, templateSubject),
  };

  return (
    <div
      className={classNames(classes.root, className)}
    >
      <NewTemplateDialog
        isOpen={newTemplateDialogOpen}
        closeModal={toggleTemplateDialog}
        onSubmit={templateName => handleCreateNewTemplate(templateName)}
      />
      {confirmDialog.visible && (
        <ConfirmationDialog
          onConfirm={async () => {
            const confirmAction = confirmationActions[confirmDialog.action];
            await confirmAction(selectedTemplate.resourceId, templateDraftContent);
            setConfirmDialog({ visible: false });
          }}
          onCancel={() => setConfirmDialog({ visible: false })}
          title={'Are you sure?'}
          message={`Are you sure you want to ${confirmDialog.action} your ${selectedTemplate.attr('isPinned') === 'true' ? 'pinned' : ''} "${selectedTemplate.attr('name')}" template?`}
          cancelLabel={'Cancel'}
          confirmLabel={confirmDialog.action.charAt(0).toUpperCase() + confirmDialog.action.slice(1)}
          ConfirmButtonProps={{
            color: confirmBeforeInsert ? 'primary' : 'red',
          }}
        />)
      }
      {useLegacyButton ? (
        <IconButton
          aria-label="more"
          aria-controls="long-menu"
          aria-haspopup="true"
          size={menuIconSize}
          title={'View Template Options'}
          icon={menuIcon}
          loading={isInitializing}
          onClick={handleOptionsMenuClick}
          {...DataValueMap.init('template-options-menu').build()}
          {...ButtonProps}
        />
      ) : (
        <FDSecondaryActionIconButton
          icon={'fileCopy'}
          label={'Templates'}
          {...DataValueMap.init('template-options-menu').build()}
          onClick={handleOptionsMenuClick}
        />
      )}

      <Menu
        anchorEl={anchorElement}
        open={isOpen}
        onClose={closeOptionsMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        disableEnforceFocus
        disableAutoFocus
        autoFocus={false}
        disableRestoreFocus
        transitionDuration={300}
      >
        {!showTokenMenuOptionOnly
          && (
            <FDBox>
              <TemplateSearchInput
                searchTemplates={searchTemplates}
                templateCollection={templates}
                setTemplateCollection={setTemplatesCollection}
                setAllTemplatesCurrentPageNumber={setAllTemplatesCurrentPageNumber}
              />
              {PinnedTemplatesListComponent}
              <TemplateList
                menuTitle={'All Templates'}
                templateCollection={templates}
                listItemOnClick={handleInsertTemplate}
                togglePinTemplate={togglePinTemplate}
                onPaginationChange={(newPageNumber) => handlePaginationChangeForAllTemplates(newPageNumber)}
                showPagination
                listType={'insert'}
                placeholder={'No saved templates'}
                isLoading={templatesLoading}
                currentPageNumber={allTemplatesCurrentPageNumber}
                loadingPrevOrNextPage={loadingAllTemplatesPrevOrNextPage}
                hasPrevPage={!!templates?.link('prev')}
                hasNextPage={!!templates?.link('next')}
                fixedHeight
              />
              <NestedMenuItem
                className={classes.menuButton}
                label="Save Draft as Template"
                parentMenuOpen={isOpen}
                leftIcon={<SaveAltIcon />}
                MenuItemProps={{
                  test: 'save-draft-as-template-menu-item',
                }}
              >
                <TemplateList
                  menuTitle={'Overwrite Template'}
                  templateCollection={templates}
                  listItemOnClick={handleTemplateOverwrite}
                  listType={'overwrite'}
                  placeholder={'No saved templates to overwrite'}
                />
                <MenuItem
                  disabled={!templateDraftContent}
                  onClick={toggleTemplateDialog}
                  test={'save-draft-as-template-button'}
                  classes={{
                    root: classes.menuButton,
                  }}
                >
                  <div className={classes.menuItemLeftIcon}>
                    <SaveAltIcon />
                  </div>
                  {'Save Draft as New Template'}
                </MenuItem>
              </NestedMenuItem>
              <NestedMenuItem
                className={classes.menuButton}
                label="Delete Template"
                parentMenuOpen={isOpen}
                leftIcon={<DeleteOutlineIcon />}
                MenuItemProps={{
                  test: 'remove-draft-as-template-menu-item',
                }}
              >
                <TemplateList
                  menuTitle={'Remove Template'}
                  templateCollection={templates}
                  listItemOnClick={handleTemplateRemoval}
                  listType={'remove'}
                  placeholder={'No saved templates to remove'}
                />
              </NestedMenuItem>
            </FDBox>
          )}
        {onInsertToken && templateDictionary?.resources()?.head() && (
          <NestedMenuItem
            className={classes.menuButton}
            label="Insert Token"
            leftIcon={<PostAddIcon />}
            parentMenuOpen={isOpen}
            MenuItemProps={{
              test: 'insert-token-menu-item',
            }}
          >
            <TemplateList
              menuTitle={'Insert Token'}
              templateCollection={templateDictionary.resources().head().attr('tokens')}
              listItemOnClick={handleInsertToken}
              listType={'overwrite'}
              placeholder={'No tokens'}
              labelAttr={'label'}
              test={'insert-token-button'}
              classes={{ root: classes.menuButton }}
            />
          </NestedMenuItem>
        )}
      </Menu>
    </div>
  );
};

TemplateOptionsMenu.defaultProps = {
  className: '',
  useLegacyButton: false,
  onInsertTemplate: () => { },
  onInsertToken: null,
  templateDraftContent: '',
  templateSubject: null,
  templateType: TEMPLATE_TYPES.messageMeetingCombo,
  confirmBeforeInsert: false,
  menuIcon: TEMPLATE_MENU_ICON_OPTIONS.default,
  menuIconSize: 'small',
  showTokenMenuOptionOnly: false,
};

TemplateOptionsMenu.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  useLegacyButton: PropTypes.bool,
  onInsertTemplate: PropTypes.func,
  templateDraftContent: PropTypes.string,
  templateType: PropTypes.oneOf([
    TEMPLATE_TYPES.note,
    TEMPLATE_TYPES.messageMeetingCombo,
  ]),
  confirmBeforeInsert: PropTypes.bool,
  templateSubject: PropTypes.string,
  onInsertToken: PropTypes.func,
  menuIcon: PropTypes.oneOf([
    TEMPLATE_MENU_ICON_OPTIONS.default,
    TEMPLATE_MENU_ICON_OPTIONS.description,
  ]),
  menuIconSize: PropTypes.oneOf([
    'small', 'medium', 'large',
  ]),
  showTokenMenuOptionOnly: PropTypes.bool,
};

export default withStyles(styles)(TemplateOptionsMenu);
